/**
 * Every export in one menu: an arrow beside the view menu's eye opens a card
 * of the 2D plan formats, the 3D model formats, and, where the menu has JSON,
 * JSON, and, where the editor can change, its reset item, whichever view is
 * showing. The room editor and the assembler each fill
 * it with their own files: the room, or the level on screen and, in advanced
 * mode, every room.
 *
 * The room editor's Paste JSON is offered unless the clipboard is known to
 * hold no JSON at all; it validates on the click and, when the JSON won't do,
 * says why in the card and changes nothing.
 *
 * The items, the files and the JSON are editor-core's export module, shared
 * with Kai Pro's menu; this one adds the browser's clipboard and download.
 */

import { css } from '@emotion/react';
import {
  copyJsonOutcome,
  EXPORT_FORMATS,
  exportFile,
  type ExportFormat,
  exportMenuGroups,
  type ExportMenuItem,
  type ExportMenuJson,
  type ExportMenuReset,
  type JsonPasteOutcome,
  looksLikeJson,
  pasteJsonOutcome,
  roomExportMenuJson,
  roomExportMenuReset,
  useFloorPlanEditorState,
} from '@kaiizen/kai-floor-plan-editor-core';
import { type ReactNode, useState } from 'react';
import { error, midtone } from '../styles';
import { copyText, readClipboardText, useClipboardText } from './clipboard';
import { downloadFile } from './downloadFile';
import { MenuHead, MenuItem, OverlayMenu } from './OverlayMenu';

const STATUS_STYLE = css`
  label: EXPORT_MENU_STATUS_STYLE;
  margin: 6px 0 0; color: ${midtone}; white-space: pre-wrap; overflow-wrap: anywhere;
`;

const STATUS_ERROR_STYLE = css`
  label: EXPORT_MENU_STATUS_ERROR_STYLE;
  color: ${error};
`;

function ExportIcon(): React.JSX.Element {
  return (
    <svg viewBox='0 0 16 16' aria-hidden='true'>
      <path d='M8 2V10M4.5 6.5L8 10L11.5 6.5M2.5 11V13.5H13.5V11' fill='none' stroke='currentColor' strokeWidth='1.5' />
    </svg>
  );
}

function MenuGroup({ label, children }: { label: string; children: ReactNode }): React.JSX.Element {
  return (
    <div role='group' aria-label={label}>
      <MenuHead>{label}</MenuHead>
      {children}
    </div>
  );
}

export interface ExportMenuProps {
  /** Downloads the room, or the level on screen, in a 2D plan or 3D model format. */
  onExport: (format: ExportFormat) => void;
  /** The JSON group's content; the menu has no JSON group without it. */
  jsonMenu?: ExportMenuJson;
  /** The reset item at the very bottom; the menu has none without it. */
  resetMenu?: ExportMenuReset;
}

/** The export menu's items (core's `exportMenuGroups`): the 2D and 3D formats, then, with `jsonMenu`, JSON to download, copy, and paste, and, with `resetMenu`, its reset item. */
function ExportMenuItems({ onExport, jsonMenu, resetMenu }: ExportMenuProps): React.JSX.Element {
  const [status, setStatus] = useState<JsonPasteOutcome | null>(null);
  const pasteJson = jsonMenu?.pasteJson;
  const clipboard = useClipboardText(pasteJson !== undefined);
  const copy = async () => setStatus(copyJsonOutcome(await copyText(jsonMenu!.json())));
  const paste = async (apply: (text: string) => JsonPasteOutcome) => setStatus(pasteJsonOutcome(await readClipboardText(), apply));
  const menuItem = ({ id, label }: ExportMenuItem): React.JSX.Element => {
    if (id === 'download-json') {
      return <MenuItem key={id} onSelect={() => downloadFile(exportFile(jsonMenu!.jsonFileName, 'json', jsonMenu!.json()))}>{label}</MenuItem>;
    }
    if (id === 'copy-json') return <MenuItem key={id} keepOpen onSelect={() => void copy()}>{label}</MenuItem>;
    if (id === 'paste-json') {
      return (
        <MenuItem
          key={id}
          keepOpen
          disabled={clipboard !== null && !looksLikeJson(clipboard)}
          title='Replace with the JSON on the clipboard'
          onSelect={() => pasteJson && void paste(pasteJson)}
        >
          {label}
        </MenuItem>
      );
    }
    if (id === 'reset-room' || id === 'reset-layout') return <MenuItem key={id} onSelect={() => resetMenu?.reset()}>{label}</MenuItem>;
    return <MenuItem key={id} onSelect={() => onExport(id)}>{label}</MenuItem>;
  };
  return (
    <>
      {exportMenuGroups({ formats: EXPORT_FORMATS, json: jsonMenu !== undefined, paste: pasteJson !== undefined, reset: resetMenu?.id }).map(group => (
        <MenuGroup key={group.label} label={group.label}>{group.items.map(menuItem)}</MenuGroup>
      ))}
      {status && <p css={[STATUS_STYLE, !status.ok && STATUS_ERROR_STYLE]} role='status' data-testid='export-menu-status'>{status.message}</p>}
    </>
  );
}

/** The export menu's button and card. The card's items, and the clipboard check behind Paste JSON, are mounted only while it is open. */
export function ExportMenu(props: ExportMenuProps): React.JSX.Element {
  return (
    <OverlayMenu label='Export' icon={<ExportIcon />} testId='export-menu-button'>
      <ExportMenuItems {...props} />
    </OverlayMenu>
  );
}

/** The room editor's export menu: the room in every format, the room's JSON to download, copy, or paste over it, and Reset this room. */
export function RoomExportMenu({ onExport }: { onExport: (format: ExportFormat) => void }): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  return <ExportMenu onExport={onExport} jsonMenu={roomExportMenuJson(floorPlanEditorState)} resetMenu={roomExportMenuReset(floorPlanEditorState)} />;
}
